import React, { useContext, useEffect, useRef, useState } from "react"
import { ArticleTemplateProps } from "./templates"
import { graphql } from "gatsby"
import { Layout } from "../components/atoms/Layout"
import { SEO } from "../components/atoms/SEO"
import BlockContent from "../components/atoms/BlockContent"
import { Image } from "../components/atoms/Image"
import tw from "twin.macro"
import { DealersNearYou } from "../components/organisms/DealersNearYou"
import {
  getCTALinkValue,
  getTealAnalyticsIDForDNYCTA,
  parseDisclaimerBlocks,
  windowExists,
} from "../helpers"
import { CTA } from "../global"
import useTealiumView from "../hooks/Tealium/useTealiumView"
import { LocationContext } from "../contexts/Location"
import useDealers from "../hooks/useDealers"
import useTealiumNoZip from "../hooks/Tealium/useTealiumNoZip"
import { LanguageContext } from "../contexts/Language"
import Coupons from "../components/molecules/Coupons/Coupons"
import { DisclaimersContext } from "../contexts/Disclaimers"
import { toggleDisclaimersModal } from "../contexts/Disclaimers/actions"
import FeaturedSnippetModule from "../components/organisms/FeaturedSnippet/FeaturedSnippetModule"
import CarCareBreadcrumb from "../components/molecules/CarCareBreadcrumb/CarCareBreadcrumb"
import PopularTips from "../components/organisms/CarCareTips/PopularTips"
import useTealiumEvent from "../hooks/Tealium/useTealiumEvent"
import { useTealiumContext } from "../contexts/Tealium"
import CarCareBackButton from "../components/molecules/CarCareBackButton/CarCareBackButton"
import { ButtonLink } from "../components/atoms/Button"

const ArticlePage: React.FC<ArticleTemplateProps> = ({
  data,
  location,
  pageContext,
}: any) => {
  const { page, carCareSubNav, articleRoutes } = data
  const { language, _ } = useContext(LanguageContext)
  const [state, dispatch] = useContext(DisclaimersContext)
  const [topic, setTopic] = useState("main")
  const { pathname } = location

  useEffect(() => {
    if (windowExists() && location?.hash?.includes("#dealersNearYou")) {
      const dealersNearYouEl = document.getElementById("dealersNearYou")!
      const dealersNearYouPosition = dealersNearYouEl.getBoundingClientRect()
      window.scroll({
        top:
          dealersNearYouPosition.top +
          window.innerHeight * 0.85 +
          window.scrollY,
        behavior: "smooth",
      })
    }
  }, [])

  //Tealium

  const { handleTealView } = useTealiumView()
  const [{ dealers }] = useContext(LocationContext)
  const pageload = useRef(true)
  const { getDealerInfo } = useDealers()
  const [isOutOfArea] = useTealiumNoZip()

  useEffect(() => {
    const breadcrumbs = pathname.replace("/es", "").split("/").filter(Boolean)
    const topic = breadcrumbs.length == 2 ? "main" : breadcrumbs[1]
    setTopic(topic)

    const dealerInfo = getDealerInfo()
    // Fire only on inital page load, and not every time dealers is updated
    if (pageload.current && dealers) {
      pageload.current = false
      handleTealView({
        tealium_event: "car_care_article",
        page_name: page?.title,
        search_results: isOutOfArea ? 0 : dealerInfo?.searchResults,
        dealer_code: isOutOfArea ? "NOT SET" : dealerInfo?.dealerCodes,
        dealer_name: isOutOfArea ? "NOT SET" : dealerInfo?.dealerNames,
        page_type: "car care tips",
        vehicle_category: topic,
      })
    }
  }, [dealers, isOutOfArea])

  const coupons = page.content.find(
    (section: any) => section._type === "couponsSection",
  )
  const updatedCoupons = coupons?.coupons?.map(coupon => ({
    ...coupon,
    vehicleCategory: topic,
  }))

  const couponsWithVehicleCategory = {
    ...coupons,
    coupons: updatedCoupons,
  }

  //Tealium
  const { trackTealEvent } = useTealiumEvent()
  const { tealPageData } = useTealiumContext()

  return (
    <Layout
      tealium={{
        label: "carCareTips",
        extraData: { vehicicle_category: [topic] },
      }}
    >
      <SEO
        url={location.href}
        title={page.pageTitle}
        keywords={page.seo?.keywords}
        schema={page.seo?.schema}
        description={page.seo?.description}
        canonicalPath={page.seo?.canonicalPath}
      />
      <section
        aria-label="Article Details Section"
        css={[tw`max-w-desktop px-8 mx-auto relative`, tw`lg:(px-10)`]}
      >
        {typeof window !== "undefined" && (
          <div css={[tw`flex mx-auto lg:(max-w-[1440px]) `]}>
            <CarCareBackButton pathname={window?.location?.pathname} />
          </div>
        )}
        <div
          css={[
            tw`border-t border-gray-400 flex flex-col justify-center items-center w-full pt-5 mb-10 gap-y-5 lg:(flex-row justify-between)`,
          ]}
        >
          <div css={tw`flex w-full justify-start`}>
            <CarCareBreadcrumb
              pathname={pathname}
              articleTitle={page.title}
              articleCategory={topic}
              fromArticle
            />
          </div>
          <div
            css={[
              tw`w-full flex gap-5 items-center justify-center lg:(justify-end)`,
            ]}
          >
            {page?.ctas?.length > 0 &&
              page?.ctas?.map((cta: CTA, index: number) => {
                const link = getCTALinkValue(cta)
                return (
                  <ButtonLink
                    to={link}
                    target={cta?.linkType === "external" ? "_blank" : "_self"}
                    key={cta?.title}
                    {...{ [cta?.buttonType]: true }}
                    analytics-id={`cta:hero:${index + 1}`}
                    css={[tw`w-full sm:(w-auto)`]}
                    onClick={() => {
                      trackTealEvent({
                        tealium_event:
                          cta?.linkType === "anchor"
                            ? "lander_anchor"
                            : cta?.linkType === "external"
                              ? "cta_click"
                              : "nav_click",
                        ...(cta?.linkType === "external" && {
                          coupon_module_text: cta?.title,
                        }),
                        ...(cta?.linkType === "external" && {
                          link_href: `${link}`,
                        }),
                        click_text: `parts service subnav:${cta?.title}`,
                        ...(cta?.linkType === "anchor" && {
                          anchor_button_text: cta?.title,
                        }),
                      })
                    }}
                  >
                    {cta?.title}
                  </ButtonLink>
                )
              })}
          </div>
        </div>
        <h1
          css={[
            tw`text-5xl mb-8 font-semibold w-full`,
            tw`lg:(text-6xl w-4/5)`,
          ]}
        >
          {parseDisclaimerBlocks(page.title, selection =>
            dispatch(toggleDisclaimersModal(selection)),
          )}
        </h1>
        <Image
          transparentPreview
          imageData={page.featuredImage?.image}
          css={[tw`w-full`]}
          objectFit="cover"
        />
        <div css={[tw`mt-12 text-xl w-full`, tw`lg:(text-2xl w-4/5)`]}>
          <BlockContent data={page.excerpt} />
        </div>
        <div
          css={[
            tw`text-lg pb-5`,
            coupons ? tw`flex flex-col lg:(flex-row w-auto)` : tw`flex`,
          ]}
        >
          <div css={[coupons && tw``]}>
            <SanitySections data={page} />
          </div>
          {coupons && (
            <div css={[tw`lg:w-[500px]`]}>
              <Coupons
                {...couponsWithVehicleCategory}
                fromArticle
                showTitle={false}
              />
            </div>
          )}
        </div>{" "}
      </section>
      <SanityContentSections
        data={page}
        articleRoutes={articleRoutes}
        translations={pageContext.translations}
        topic={topic}
      />
    </Layout>
  )
}
const SanitySections = ({ data }: any): JSX.Element => {
  let imageSizeStyle = {}

  switch (data.bodyImageSize) {
    case "small":
      imageSizeStyle = { width: "25%", height: "auto" }
      break
    case "medium":
      imageSizeStyle = { width: "50%", height: "auto" }
      break
    case "large":
      imageSizeStyle = { width: "100%", height: "auto" }
      break
    default:
      imageSizeStyle = {}
  }

  return (
    <div css={[tw`my-10`]}>
      {data.body.map((section: any, index: number) => (
        <React.Fragment key={section?._key || `articles-data-body-${index}`}>
          {(() => {
            switch (section._type) {
              case "block":
                return <BlockContent key={index} data={section} />
              case "image":
                return (
                  <div key={index}>
                    <Image imageData={section} css={imageSizeStyle} />
                  </div>
                )
              case "bodyImageSize":
                // Ignore the bodyImageSize section itself in rendering
                return null
              default:
                return null
            }
          })()}
        </React.Fragment>
      ))}
    </div>
  )
}

const SanityContentSections = ({
  data,
  translations,
  articleRoutes,
  topic,
}: any): JSX.Element => {
  return (
    <div css={[tw``]}>
      {data?.content?.map((section: any, index: number) => (
        <React.Fragment key={section?._key || `accessories-section-${index}`}>
          {(() => {
            switch (section?._type) {
              case "dealersNearYouSection":
                return (
                  <DealersNearYou
                    type="default"
                    {...section}
                    ctaAnalyticsId={getTealAnalyticsIDForDNYCTA(
                      section?.dealerCtaLink?.toLowerCase(),
                    )}
                    carCareTopic={topic}
                  />
                )
              case "popularTips":
                return (
                  <PopularTips
                    {...section}
                    category={"all"}
                    translations={translations}
                    articleRoutes={articleRoutes?.nodes}
                  />
                )
              case "featuredSnippetModule":
                return <FeaturedSnippetModule {...section} />
            }
          })()}
        </React.Fragment>
      ))}
    </div>
  )
}

export const query = graphql`
  query ArticlePageQuery($id: String, $lang: String) {
    page: sanityPageArticle(id: { eq: $id }) {
      id
      language
      _type
      title
      pageTitle
      excerpt: _rawExcerpt(resolveReferences: { maxDepth: 5 })
      body: _rawBody(resolveReferences: { maxDepth: 5 })
      bodyImageSize
      featuredImage {
        ...SanityImageData
      }

      categories {
        category
      }
      seo {
        keywords
        schema
        description
        canonicalPath
      }
      disclaimers {
        code
        disclaimer
      }
      ctas {
        ...CTAData
      }
      content {
        ...DealersNearYouData
        ...CouponsSectionData
        ...PopularTipsData
        ...FeaturedSnippetModuleData
      }
    }
    carCareSubNav: allSanityCarCareTipsSubNavigation(
      filter: { language: { eq: $lang } }
    ) {
      nodes {
        menuItems {
          title
          titleLink {
            slug {
              current
            }
          }
          _key
        }
        menuName
      }
    }
    articleRoutes: allSanityRoute {
      nodes {
        page {
          ... on SanityPageArticle {
            _type
            _id
          }
        }
        slug {
          current
        }
      }
    }
  }
`

export default ArticlePage
